import { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { useLanguage } from '@/context/LanguageContext';
import { chartColors } from '@/utils';
import { URGENCY_CONFIG } from '@/types';
import type { UrgencyLevel } from '@/types';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface ChartsProps {
  data: {
    totalCalls: number;
    resolvedCalls: number;
    pendingCalls: number;
    avgDuration: number;
    byDate: Record<string, unknown[]>;
    byHour: Record<number, unknown[]>;
    byDayOfWeek: Record<number, unknown[]>;
    byCategory: Record<string, number>;
    byServiceType: Record<string, number>;
    durationRanges: Record<string, number>;
    
    // New analytics data
    byUrgency?: Record<string, number>;
    highUrgencyCount?: number;
    highUrgencyRate?: number;
    atRiskCalls?: unknown[];
    atRiskCount?: number;
    byUrgencyAndStatus?: Record<string, { resolved: number; pending: number }>;
    serviceTypeEfficiency?: Record<string, { total: number; resolved: number; pending: number; avgDuration: number; resolutionRate: number }>;
    peakHours?: { hour: number; count: number }[];
    avgHandleTimeByCategory?: Record<string, number>;
    avgHandleTimeByServiceType?: Record<string, number>;
  };
  loading: boolean;
}

export function AnalyticsCharts({ data, loading }: ChartsProps) {
  const { language, t } = useLanguage();

  // Day names based on language
  const dayNames = [
    t('days.sun'),
    t('days.mon'),
    t('days.tue'),
    t('days.wed'),
    t('days.thu'),
    t('days.fri'),
    t('days.sat'),
  ];

  // Hour labels
  const hourLabels = Array.from({ length: 24 }, (_, i) => {
    if (language === 'en') {
      return i === 0 ? '12 AM' : i < 12 ? `${i} AM` : i === 12 ? '12 PM' : `${i - 12} PM`;
    }
    return `${i}:00`;
  });

  // Chart options
  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: { size: 14 },
        bodyFont: { size: 13 },
        cornerRadius: 8,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
      },
    },
    interaction: {
      intersect: false,
      mode: 'index' as const,
    },
  };

  const barOptions = {
    ...lineOptions,
    plugins: {
      ...lineOptions.plugins,
      legend: {
        display: false,
      },
    },
  };

  const horizontalBarOptions = {
    ...barOptions,
    indexAxis: 'y' as const,
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          padding: 20,
          usePointStyle: true,
          pointStyle: 'circle',
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        cornerRadius: 8,
      },
    },
    cutout: '65%',
  };

  const groupedBarOptions = {
    ...lineOptions,
    plugins: {
      ...lineOptions.plugins,
      legend: {
        display: true,
        position: 'bottom' as const,
        labels: {
          padding: 20,
          usePointStyle: true,
        },
      },
    },
  };

  // Call Volume Over Time Chart
  const callVolumeData = useMemo(() => {
    if (!data.byDate) return null;
    const sortedDates = Object.keys(data.byDate).sort();
    return {
      labels: sortedDates,
      datasets: [
        {
          label: t('metrics.totalCalls'),
          data: sortedDates.map(date => (data.byDate[date] as unknown[]).length),
          borderColor: chartColors.primary,
          backgroundColor: chartColors.primaryLight,
          fill: true,
          tension: 0.4,
        },
      ],
    };
  }, [data.byDate, t]);

  // Status Distribution Chart
  const statusData = useMemo(() => ({
    labels: [t('status.resolved'), t('status.pending')],
    datasets: [
      {
        data: [data.resolvedCalls, data.pendingCalls],
        backgroundColor: [chartColors.success, chartColors.warning],
        borderColor: [chartColors.success, chartColors.warning],
        borderWidth: 0,
      },
    ],
  }), [data.resolvedCalls, data.pendingCalls, t]);

  // NEW: Urgency Distribution Chart
  const urgencyData = useMemo(() => {
    const urgency = data.byUrgency || {};
    const levels: UrgencyLevel[] = ['no', 'low', 'medium', 'high', 'extra_high'];
    const labels = levels.map(level => URGENCY_CONFIG[level]?.label || level);
    return {
      labels,
      datasets: [
        {
          data: levels.map(level => urgency[level] || 0),
          backgroundColor: levels.map(level => URGENCY_CONFIG[level]?.bg || '#ccc'),
          borderColor: levels.map(level => URGENCY_CONFIG[level]?.color || '#999'),
          borderWidth: 1,
        },
      ],
    };
  }, [data.byUrgency]);

  // NEW: Urgency by Status Chart
  const urgencyStatusData = useMemo(() => {
    const crossTab = data.byUrgencyAndStatus || {};
    const levels: UrgencyLevel[] = ['no', 'low', 'medium', 'high', 'extra_high'];
    return {
      labels: levels.map(level => URGENCY_CONFIG[level]?.label || level),
      datasets: [
        {
          label: t('status.resolved'),
          data: levels.map(level => crossTab[level]?.resolved || 0),
          backgroundColor: chartColors.success,
          borderRadius: 4,
        },
        {
          label: t('status.pending'),
          data: levels.map(level => crossTab[level]?.pending || 0),
          backgroundColor: chartColors.warning,
          borderRadius: 4,
        },
      ],
    };
  }, [data.byUrgencyAndStatus, t]);

  // Calls by Day of Week Chart
  const callsByDayData = useMemo(() => {
    const dayCounts = Array(7).fill(0);
    Object.keys(data.byDayOfWeek).forEach(key => {
      const dayIndex = parseInt(key);
      if (!isNaN(dayIndex) && dayIndex >= 0 && dayIndex < 7) {
        dayCounts[dayIndex] = (data.byDayOfWeek[dayIndex] as unknown[]).length;
      }
    });
    return {
      labels: dayNames,
      datasets: [
        {
          label: t('metrics.totalCalls'),
          data: dayCounts,
          backgroundColor: chartColors.primary,
          borderRadius: 6,
        },
      ],
    };
  }, [data.byDayOfWeek, dayNames, t]);

  // Duration Distribution Chart
  const durationData = useMemo(() => {
    const ranges = data.durationRanges || {};
    const labels = language === 'en'
      ? ['0-30s', '30-60s', '1-2m', '2-5m', '5m+']
      : ['0-30ث', '30-60ث', '1-2د', '2-5د', '5د+'];
    return {
      labels,
      datasets: [
        {
          label: t('charts.durationDist'),
          data: [
            ranges['0-30s'] || 0,
            ranges['30-60s'] || 0,
            ranges['1-2m'] || 0,
            ranges['2-5m'] || 0,
            ranges['5m+'] || 0,
          ],
          backgroundColor: [
            chartColors.success,
            chartColors.teal,
            chartColors.primary,
            chartColors.purple,
            chartColors.pink,
          ],
          borderRadius: 6,
        },
      ],
    };
  }, [data.durationRanges, language, t]);

  // NEW: Top Peak Hours Chart (horizontal bar showing top 8 hours)
  const peakHoursData = useMemo(() => {
    const peakHours = data.peakHours || [];
    const topHours = peakHours
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);
    return {
      labels: topHours.map(h => hourLabels[h.hour]),
      datasets: [
        {
          label: t('charts.peakHours'),
          data: topHours.map(h => h.count),
          backgroundColor: chartColors.indigo,
          borderRadius: 4,
        },
      ],
    };
  }, [data.peakHours, hourLabels, t]);

  // Calls by Category Chart
  const categoryData = useMemo(() => {
    const categories = data.byCategory || {};
    const sortedCategories = Object.entries(categories)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 8);
    return {
      labels: sortedCategories.map(([label]) => label),
      datasets: [
        {
          label: t('charts.callsByCategory'),
          data: sortedCategories.map(([, value]) => value),
          backgroundColor: chartColors.primary,
          borderRadius: 6,
        },
      ],
    };
  }, [data.byCategory, t]);

  // NEW: Average Handle Time by Category Chart
  const avgHandleTimeByCategoryData = useMemo(() => {
    const avgTimes = data.avgHandleTimeByCategory || {};
    const sortedCategories = Object.entries(avgTimes)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 8);
    return {
      labels: sortedCategories.map(([label]) => label),
      datasets: [
        {
          label: t('metrics.avgDuration'),
          data: sortedCategories.map(([, value]) => value),
          backgroundColor: chartColors.purple,
          borderRadius: 6,
        },
      ],
    };
  }, [data.avgHandleTimeByCategory, t]);

  // Calls by Service Type Chart
  const serviceTypeData = useMemo(() => {
    const types = data.byServiceType || {};
    const sortedTypes = Object.entries(types)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 6);
    return {
      labels: sortedTypes.map(([label]) => label),
      datasets: [
        {
          label: t('charts.callsByService'),
          data: sortedTypes.map(([, value]) => value),
          backgroundColor: [
            chartColors.success,
            chartColors.warning,
            chartColors.primary,
            chartColors.purple,
            chartColors.pink,
            chartColors.teal,
          ],
          borderRadius: 6,
        },
      ],
    };
  }, [data.byServiceType, t]);

  // NEW: Service Type Efficiency Chart (resolution rate)
  const serviceTypeEfficiencyData = useMemo(() => {
    const efficiency = data.serviceTypeEfficiency || {};
    const sortedTypes = Object.entries(efficiency)
      .sort(([, a], [, b]) => b.resolutionRate - a.resolutionRate)
      .slice(0, 8);
    return {
      labels: sortedTypes.map(([label]) => label),
      datasets: [
        {
          label: t('status.resolved'),
          data: sortedTypes.map(([, value]) => value.resolutionRate),
          backgroundColor: chartColors.success,
          borderRadius: 4,
        },
      ],
    };
  }, [data.serviceTypeEfficiency, t]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {Array.from({ length: 10 }).map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader>
              <div className="h-4 bg-muted rounded w-1/2" />
            </CardHeader>
            <CardContent>
              <div className="h-64 bg-muted rounded" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Call Volume Over Time */}
      <Card className="col-span-1 md:col-span-2">
        <CardHeader>
          <CardTitle>{t('charts.callVolume')}</CardTitle>
          <CardDescription>{t('charts.callVolumeDesc')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            {callVolumeData && <Line data={callVolumeData} options={lineOptions} />}
          </div>
        </CardContent>
      </Card>

      {/* Status Distribution */}
      <Card>
        <CardHeader>
          <CardTitle>{t('charts.statusDist')}</CardTitle>
          <CardDescription>{t('charts.statusDistDesc')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-72">
            <Doughnut data={statusData} options={doughnutOptions} />
          </div>
        </CardContent>
      </Card>

      {/* NEW: Urgency Distribution */}
      <Card>
        <CardHeader>
          <CardTitle>{t('charts.urgencyDist')}</CardTitle>
          <CardDescription>{t('charts.urgencyDistDesc')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-72">
            <Doughnut data={urgencyData} options={doughnutOptions} />
          </div>
        </CardContent>
      </Card>

      {/* NEW: Urgency by Status */}
      <Card className="col-span-1 md:col-span-2">
        <CardHeader>
          <CardTitle>{t('charts.urgencyAnalysis')}</CardTitle>
          <CardDescription>{t('charts.urgencyAnalysisDesc')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-72">
            <Bar data={urgencyStatusData} options={groupedBarOptions} />
          </div>
        </CardContent>
      </Card>

      {/* Calls by Day of Week */}
      <Card>
        <CardHeader>
          <CardTitle>{t('charts.callsByDay')}</CardTitle>
          <CardDescription>{t('charts.callsByDayDesc')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-72">
            <Bar data={callsByDayData} options={barOptions} />
          </div>
        </CardContent>
      </Card>

      {/* Duration Distribution */}
      <Card>
        <CardHeader>
          <CardTitle>{t('charts.durationDist')}</CardTitle>
          <CardDescription>{t('charts.durationDistDesc')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-72">
            <Bar data={durationData} options={barOptions} />
          </div>
        </CardContent>
      </Card>

      {/* NEW: Top Peak Hours */}
      <Card>
        <CardHeader>
          <CardTitle>{t('charts.peakHours')}</CardTitle>
          <CardDescription>{t('charts.peakHoursDesc')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-72">
            <Bar data={peakHoursData} options={horizontalBarOptions} />
          </div>
        </CardContent>
      </Card>

      {/* Calls by Category */}
      <Card>
        <CardHeader>
          <CardTitle>{t('charts.callsByCategory')}</CardTitle>
          <CardDescription>{t('charts.callsByCategoryDesc')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-72">
            <Bar data={categoryData} options={barOptions} />
          </div>
        </CardContent>
      </Card>

      {/* NEW: Average Handle Time by Category */}
      <Card>
        <CardHeader>
          <CardTitle>{t('charts.avgHandleTime')}</CardTitle>
          <CardDescription>{t('charts.avgHandleTimeDesc')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-72">
            <Bar data={avgHandleTimeByCategoryData} options={horizontalBarOptions} />
          </div>
        </CardContent>
      </Card>

      {/* Calls by Service Type */}
      <Card>
        <CardHeader>
          <CardTitle>{t('charts.callsByService')}</CardTitle>
          <CardDescription>{t('charts.callsByServiceDesc')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-72">
            <Bar data={serviceTypeData} options={barOptions} />
          </div>
        </CardContent>
      </Card>

      {/* NEW: Service Type Efficiency */}
      <Card className="col-span-1 md:col-span-2">
        <CardHeader>
          <CardTitle>{t('charts.serviceEfficiency')}</CardTitle>
          <CardDescription>{t('charts.serviceEfficiencyDesc')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-72">
            <Bar data={serviceTypeEfficiencyData} options={horizontalBarOptions} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
